import React from 'react';
import styled from 'styled-components';
import {
  selectChartType,
  selectedChartWidget,
  selectWidgetView,
  updateDisplayType,
} from '@/features/dashboard/dashboardSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  TChartType,
  useGetAvailableChartsQuery,
} from '@/features/chart-configuration/apiSlice';
import { useDatasourceId } from '@/hooks';
import { UUID } from '@/types';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
  PIVOT,
  TABLE,
} from '@/components/charting/chartType.constants';
import { Island } from '@trevor/ui-components';
import {
  Bar,
  BarLine,
  Line,
  Pie,
  Pivot,
  SingleValue,
  Stacked,
  Table,
} from '@/features/dataPresentation/navigation/buttons';
import { useSwitchChartType_LEGACY } from '@/features/dashboard/components/SwitchChartType/useSwitchChartType_LEGACY';
import { openReactModal } from '@/actions';
import ChartHint from '@/components/ChartHint';
import { ChartType } from '@/components/charting/types';
import { SINGLE_VALUE } from '@/components/ChartHolder.constants';

const Container = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: var(--spacing-xl);

  > div {
    margin-left: var(--spacing-xl);
  }
`;

export const SwitchChartType = () => {
  const chartType = useAppSelector(selectChartType);
  const dispatch = useAppDispatch();
  const datasourceId = useDatasourceId();
  const view = useAppSelector(selectWidgetView);
  const widgetKey = useAppSelector(selectedChartWidget)?.key;

  const { data } = useGetAvailableChartsQuery(
    {
      datasourceId,
      seriesId: view?._series as UUID,
    },
    { skip: !view?._series },
  );

  useSwitchChartType_LEGACY();

  if (!data) return null;

  const onChangeDataPresentation = (newDisplayType: TChartType) => () => {
    dispatch(updateDisplayType(newDisplayType));
  };

  const switchToTable = () => {
    onChangeDataPresentation(TABLE)();
    dispatch({
      type: 'UPDATE_WIDGET_CHART_CONFIGS',
      payload: {
        widgetKey,
        chartType: TABLE,
        metrics: null,
        dimensions: null,
      },
    });
  };

  const openPresentationHint = (presentation: ChartType) => () =>
    dispatch(openReactModal(<ChartHint presentation={presentation} />));

  const isPivotPossible = PIVOT in data;
  const isSingleValuePossible = SINGLE_VALUE in data;
  const isPiePossible = PIE in data;
  const isBarPossible = BAR in data;
  const isLinePossible = LINE in data;
  const isBarLinePossible = BARLINE in data;
  const isStackedPossible = 'stacked' in data;

  return (
    <Container>
      Display as
      <Island size="small">
        <Table onClick={switchToTable} $active={chartType === TABLE} />
        <Pivot
          onClick={
            isPivotPossible
              ? onChangeDataPresentation(PIVOT)
              : openPresentationHint(PIVOT)
          }
          $active={chartType === PIVOT}
          $static={!isPivotPossible}
        />
        <SingleValue
          onClick={
            isSingleValuePossible
              ? onChangeDataPresentation(SINGLE_VALUE)
              : openPresentationHint(TABLE)
          }
          $active={chartType === SINGLE_VALUE}
          $static={!isSingleValuePossible}
        />
        <Pie
          onClick={
            isPiePossible
              ? onChangeDataPresentation(PIE)
              : openPresentationHint(PIE)
          }
          $active={chartType === PIE}
          $static={!isPiePossible}
        />
        <Bar
          onClick={
            isBarPossible
              ? onChangeDataPresentation(BAR)
              : openPresentationHint(BAR)
          }
          $active={chartType === BAR}
          $static={!isBarPossible}
        />
        <Line
          onClick={
            isLinePossible
              ? onChangeDataPresentation(LINE)
              : openPresentationHint(LINE)
          }
          $active={chartType === LINE}
          $static={!isLinePossible}
        />
        <BarLine
          onClick={
            isBarLinePossible
              ? onChangeDataPresentation(BARLINE)
              : openPresentationHint(BARLINE)
          }
          $active={chartType === BARLINE}
          $static={!isBarLinePossible}
        />
        <Stacked
          onClick={
            isStackedPossible
              ? onChangeDataPresentation('stacked')
              : openPresentationHint('stacked')
          }
          $active={chartType === 'stacked'}
          $static={!isStackedPossible}
        />
      </Island>
    </Container>
  );
};
